
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PanelComponent } from '@components/core-template/panel/panel.component';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from "@ng-select/ng-select";
import { UserService } from '@services/core/user.service';

@Component({
  selector: 'app-user-menus',
  templateUrl: './menu.component.html',
  styleUrls: ["./menu.component.css"],
  imports: [FormsModule, ReactiveFormsModule, NgbTooltipModule, PanelComponent, NgSelectComponent],
  providers: [UserService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenusComponent implements OnInit {

  private _modalService = inject(NgbModal)
  private _fb = inject(FormBuilder)
  private _userService = inject(UserService)
  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)

  protected menuForm = this._fb.group({
    _id: [null],
    parentId: [null],
    title: [null, Validators.required],
    icon: [null],
    url: [null],
  })

  protected catalogo: any[] = []
  protected assignedMenus: any[] = []
  protected roles: any[] = []
  protected selectedRoleId: string = ''
  protected assignments: { [menuId: string]: boolean } = {}

  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: true
  }

  ngOnInit(): void {
    this.getRoles()
    this.getCatalogo()
  }

  open(modal: any, item?: any) {
    this.menuForm.reset()
    if (item) this.menuForm.patchValue(item)
    this._modalService.open(modal, this.modalOptions)
  }

  getRoles() {
    this._userService.getRoles().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: any) => {
      this.roles = roles
      if (roles.length > 0) {
        this.selectedRoleId = roles[0]._id
        this.getRoleMenu()
      }
      this.cdr.markForCheck()
    })
  }

  getCatalogo() {
    this._userService.getAllMenus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menus: any) => {
      this.catalogo = menus
      this.cdr.markForCheck()
    })
  }

  saveMenu() {
    if (!this.menuForm.valid) return
    const val = this.menuForm.value
    if (val._id) {
      this._userService.updateMenu(val).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.getCatalogo()
        this.menuForm.reset()
      })
    } else {
      this._userService.createMenu(val).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.getCatalogo()
        this.menuForm.reset()
      })
    }
  }

  deleteMenu(id: string) {
    this._userService.deleteMenu(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getCatalogo()
      this.getRoleMenu()
    })
  }

  getRoleMenu() {
    if (!this.selectedRoleId) return
    this._userService.getRoleMenu(this.selectedRoleId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.assignedMenus = res.menus
      this.assignments = res.assigned
      this.cdr.markForCheck()
    })
  }

  toggleMenu(menuId: string, enabled: boolean) {
    this._userService.toggleRoleMenu(this.selectedRoleId, menuId, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.assignments = res.assigned
      this.cdr.markForCheck()
    })
  }

  getEnabled(menuId: string): boolean {
    return this.assignments[menuId] === true
  }

  getEnabledCount(): number {
    return Object.values(this.assignments).filter(v => v === true).length
  }

  get selectedRoleName(): string {
    const role = this.roles.find(r => r._id === this.selectedRoleId)
    return role ? role.name : ''
  }

  /* ───── Orden ───── */

  moveUp(item: any, parent?: any): void {
    const siblings: any[] = parent ? parent.submenu : this.catalogo
    const idx = siblings.indexOf(item)
    if (idx <= 0) return
    this.swapIdx(idx, idx - 1, siblings)
  }

  moveDown(item: any, parent?: any): void {
    const siblings: any[] = parent ? parent.submenu : this.catalogo
    const idx = siblings.indexOf(item)
    if (idx < 0 || idx >= siblings.length - 1) return
    this.swapIdx(idx, idx + 1, siblings)
  }

  private swapIdx(i1: number, i2: number, siblings: any[]): void {
    [siblings[i1], siblings[i2]] = [siblings[i2], siblings[i1]]
    const sorted = [...siblings]

    if (siblings === this.catalogo) {
      this.catalogo = sorted
    } else {
      this.catalogo = this.catalogo.map(p => p.submenu === siblings ? { ...p, submenu: sorted } : p)
    }

    this._userService.reorderMenus(sorted.map(s => s._id)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getCatalogo()
      this.getRoleMenu()
    })
  }
}
